import { OfficeViewer } from '@components/shared/OfficeViewer';
import { PdfViewer } from '@components/shared/PdfViewer';
import { getIcon } from '@features/requests/incomingRequests/incomingRequests';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { isOfficeFile, isPdfFile } from '@utils/fileUtils';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';

interface AttachmentsProps {
	files?: string[];
}

export const Attachments: React.FC<AttachmentsProps> = ({ files = [] }) => {
	const { t } = useTranslation();
	const [mediaIndex, setMediaIndex] = React.useState(-1);

	// PDF Viewer state
	const [pdfViewerOpen, setPdfViewerOpen] = React.useState(false);
	const [currentPdfUrl, setCurrentPdfUrl] = React.useState('');
	const [currentPdfFileName, setCurrentPdfFileName] = React.useState('');

	// Office Viewer state
	const [officeViewerOpen, setOfficeViewerOpen] = React.useState(false);
	const [currentOfficeUrl, setCurrentOfficeUrl] = React.useState('');
	const [currentOfficeFileName, setCurrentOfficeFileName] = React.useState('');

	const groupFilesByType = useMemo(() => {
		return {
			images: files.filter((file: string) => {
				const ext = file.split('.').pop()?.toLowerCase();
				return ['jpg', 'jpeg', 'png', 'gif'].includes(ext || '');
			}),
			documents: files.filter((file: string) => {
				const ext = file.split('.').pop()?.toLowerCase();
				return ['pdf', 'doc', 'docx', 'txt', 'xls', 'xlsx'].includes(ext || '');
			}),
			videos: files.filter((file: string) => {
				const ext = file.split('.').pop()?.toLowerCase();
				return ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'm4v', '3gp'].includes(ext || '');
			}),
		};
	}, [files]);

	// Combined media (images and videos) for lightbox
	const mediaFiles = useMemo(() => {
		return [
			...groupFilesByType.images.map((file: string) => ({ file, type: 'image' })),
			...groupFilesByType.videos.map((file: string) => ({ file, type: 'video' })),
		];
	}, [groupFilesByType.images, groupFilesByType.videos]);

	const mediaSlides = useMemo(() => {
		return mediaFiles.map((media) => {
			const url = `${process.env.REACT_APP_CDN_URL}${media.file}`;
			const fileName = media.file.replace('uploads/', '');
			
			if (media.type === 'video') {
				// Get video file extension for proper MIME type
				const extension = media.file.split('.').pop()?.toLowerCase();
				const mimeType = extension === 'webm' ? 'video/webm' : 
								extension === 'mov' ? 'video/quicktime' :
								extension === 'avi' ? 'video/x-msvideo' :
								extension === 'wmv' ? 'video/x-ms-wmv' :
								extension === 'mkv' ? 'video/x-matroska' :
								'video/mp4';
				
				return {
					type: 'video' as const,
					width: 1280,
					height: 720,
					poster: url, // Use video URL as poster for now
					sources: [
						{
							src: url,
							type: mimeType,
						},
					],
				};
			} else {
				return {
					src: url,
					description: fileName,
				};
			}
		});
	}, [mediaFiles]);

	const handleFileClick = (file: string) => {
		const fileUrl = `${process.env.REACT_APP_CDN_URL}${file}`;
		const fileName = file.replace('uploads/', '');

		if (isPdfFile(fileName)) {
			setCurrentPdfUrl(fileUrl);
			setCurrentPdfFileName(fileName);
			setPdfViewerOpen(true);
		} else if (isOfficeFile(fileName)) {
			setCurrentOfficeUrl(fileUrl);
			setCurrentOfficeFileName(fileName);
			setOfficeViewerOpen(true);
		} else {
			window.open(fileUrl, '_blank');
		}
	};

	const handleMediaClick = (type: 'image' | 'video', index: number) => {
		// Calculate the actual index in the combined media array
		const imageCount = groupFilesByType.images.length;
		const actualIndex = type === 'image' ? index : imageCount + index;
		setMediaIndex(actualIndex);
	};

	return (
		<>
			<div className="flex flex-col gap-[20px] pb-5">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('attachments')}</h6>
				{groupFilesByType.documents.map((file: string, index: number) => (
					<div className="flex gap-[10px]" key={index} onClick={() => handleFileClick(file)}>
						<img src={getIcon(file.split('.').pop()?.toLowerCase() as any)} alt={file} />
						<span className="text-secondary roboto-regular text-[16px] leading-normal underline">
							{file.replace('uploads/', '')}
						</span>
					</div>
				))}
			</div>
			<div className="flex flex-wrap gap-[10px]">
				<div className="flex flex-wrap gap-[10px] mt-[10px]">
					{groupFilesByType.images.map((resource: string, index: number) => (
						<div
							className="group relative w-[77px] h-[77px] rounded-[12px] shadow-custom-button cursor-pointer"
							key={index}
							onClick={() => handleMediaClick('image', index)}
						>
							<img
								className="w-full h-full rounded-[12px] object-cover"
								src={`${process.env.REACT_APP_CDN_URL}${resource}`}
								alt={`resource-${index}`}
							/>
							<div className="absolute inset-0 bg-[rgba(3,136,119,0.7)] rounded-[12px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
								<ZoomInIcon className="text-white text-[36px]" />
							</div>
						</div>
					))}
				</div>
				<div className="flex flex-wrap gap-[10px] mt-[10px]">
					{groupFilesByType.videos.map((resource: string, index: number) => (
						<div
							className="group relative w-[77px] h-[77px] rounded-[12px] shadow-custom-button cursor-pointer"
							key={index}
							onClick={() => handleMediaClick('video', index)}
						>
							<div className="w-full h-full rounded-[12px] bg-gray-200 flex items-center justify-center overflow-hidden">
								<video
									className="w-full h-full object-cover"
									src={`${process.env.REACT_APP_CDN_URL}${resource}`}
									muted
								/>
								<div className="absolute inset-0 flex items-center justify-center">
									<PlayArrowIcon className="text-white text-[36px] drop-shadow-lg" />
								</div>
							</div>
							<div className="absolute inset-0 bg-[rgba(3,136,119,0.7)] rounded-[12px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
								<PlayArrowIcon className="text-white text-[36px]" />
							</div>
						</div>
					))}
				</div>
			</div>

			<Lightbox
				index={mediaIndex}
				slides={mediaSlides}
				open={mediaIndex >= 0}
				close={() => setMediaIndex(-1)}
				plugins={[Video]}
			/>

			{/* PDF Viewer component for viewing PDFs */}
			<PdfViewer
				open={pdfViewerOpen}
				onClose={() => setPdfViewerOpen(false)}
				pdfUrl={currentPdfUrl}
				fileName={currentPdfFileName}
			/>

			{/* Office Viewer component for viewing Office documents */}
			<OfficeViewer
				open={officeViewerOpen}
				onClose={() => setOfficeViewerOpen(false)}
				documentUrl={currentOfficeUrl}
				fileName={currentOfficeFileName}
			/>
		</>
	);
};
